import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import { FormField } from "@babackapp/ui/components/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import type { ActionNodeReturnAutoTreatConfigDto } from "@babackapp/private-api-client/types";
import {
  PanelContainer,
  NodePanelProps,
} from "../../lib/panel/panel-container";
import { PanelContent } from "../../lib/panel/panel-content";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";

const MODES = ["mark_treated", "accept_items"] as const;

function PanelAutoTreatMode() {
  const form = useWorkflowForm();
  const { t } = useTranslation("trackoffice");
  const fieldPath = useNodeFormPath("config.mode");

  if (!fieldPath) {
    return null;
  }

  return (
    <PanelContent
      title={t("workflowsEditor.returnAutoTreat.title", "Auto-treatment")}
      description={t(
        "workflowsEditor.returnAutoTreat.description",
        "How the return is treated when the workflow runs.",
      )}
    >
      <FormField
        control={form.control}
        name={fieldPath}
        render={({ field }) => {
          const mode = (field.value as string) || "mark_treated";
          return (
            <>
              <Select value={mode} onValueChange={field.onChange}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((value) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs">
                          {t(`workflowsEditor.returnAutoTreat.modes.${value}`)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {t(
                            `workflowsEditor.returnAutoTreat.modeDescriptions.${value}`,
                          )}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode === "accept_items" && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400">
                  <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                  <span>
                    {t(
                      "workflowsEditor.returnAutoTreat.acceptItemsWarning",
                      "Every item of the return will be accepted and processed (refund, exchange…) without manual review.",
                    )}
                  </span>
                </div>
              )}
            </>
          );
        }}
      />
    </PanelContent>
  );
}

export function ReturnAutoTreatActionNodePanel(
  props: NodePanelProps<ActionNodeReturnAutoTreatConfigDto>,
) {
  return (
    <PanelContainer nodeProps={props}>
      <PanelAutoTreatMode />
    </PanelContainer>
  );
}
